import { oids } from "@/configs/Settings";
import { GetSchedule, PatchSchedule } from "@/services/fhir";
import { TELECONSULTORIA_SERVICE_CATEGORY_TOKEN } from "../constants";
import type { TeleconsultoriaAvailableProfessional } from "../types";
import {
  actorByType,
  guestFromSchedule,
  isTeleconsultoriaAvailabilitySchedule,
} from "../utils/actors";
import { organizationIdFromReference } from "./resolveOrganizationDisplays";

export function schedulesFromBundle(bundle?: fhir5.Bundle): fhir5.Schedule[] {
  return (bundle?.entry || [])
    .map((entry) => entry.resource)
    .filter((resource): resource is fhir5.Schedule => {
      return resource?.resourceType === "Schedule";
    });
}

export function healthcareServicesFromBundle(
  bundle?: fhir5.Bundle,
): fhir5.HealthcareService[] {
  return (bundle?.entry || [])
    .map((entry) => entry.resource)
    .filter((resource): resource is fhir5.HealthcareService => {
      return resource?.resourceType === "HealthcareService";
    });
}

/** HCS do Schedule, se veio no bundle via _include. */
export function includedHealthcareService(
  schedule: fhir5.Schedule,
  services: fhir5.HealthcareService[],
): fhir5.HealthcareService | undefined {
  const id = actorByType(schedule.actor, "HealthcareService")?.reference.split(
    "/",
  )[1];
  if (!id) return undefined;
  return services.find((service) => service.id === id);
}

/**
 * Schedule de DISPONIBILIDADE do profissional e o HCS incluído no bundle.
 * A agenda com horários (com Location) também tem a categoria teleconsultoria,
 * mas não é a do switch: alternar ela desligaria os horários.
 */
export async function fetchMyTeleconsultoria(params: {
  practitionerRef: string;
  organizationToken: string;
}): Promise<{
  schedule?: fhir5.Schedule;
  healthcareService?: fhir5.HealthcareService;
}> {
  const bundle = await GetSchedule({
    practitioner: params.practitionerRef,
    organization: params.organizationToken,
    serviceCategory: TELECONSULTORIA_SERVICE_CATEGORY_TOKEN,
    includeHealthcareService: true,
    count: 20,
  });

  const schedule = schedulesFromBundle(bundle).find(
    (item) =>
      isTeleconsultoriaAvailabilitySchedule(item) &&
      actorByType(item.actor, "Practitioner")?.reference ===
        params.practitionerRef,
  );
  if (!schedule) return {};
  return {
    schedule,
    healthcareService: includedHealthcareService(
      schedule,
      healthcareServicesFromBundle(bundle),
    ),
  };
}

/**
 * Convidáveis de qualquer organização.
 * Elegibilidade só pelo Schedule ativo (e HCS incluído). Não lê Contract.
 * Só a agenda de disponibilidade conta: a agenda com horários do mesmo
 * profissional duplicaria a linha no picker.
 *
 * `organizationTokens` (opcional): restringe às organizações permitidas pelo
 * perfil (afiliada → prestadora dela; prestadora → ela mesma). Lista vazia
 * devolve nada. O filtro vale na busca E no cliente: nunca mostra outra organização.
 */
export async function listAvailableTeleconsultoria(params: {
  hostPractitionerRef: string;
  organizationTokens?: string[];
}): Promise<TeleconsultoriaAvailableProfessional[]> {
  const allowedTokens = params.organizationTokens?.filter(Boolean);
  if (allowedTokens && !allowedTokens.length) return [];

  const bundle = await GetSchedule({
    active: true,
    serviceCategory: TELECONSULTORIA_SERVICE_CATEGORY_TOKEN,
    includeHealthcareService: true,
    count: 250,
    ...(allowedTokens && { organization: allowedTokens }),
  });
  const services = healthcareServicesFromBundle(bundle);
  const isAllowedOrganization = (organizationId: string) =>
    !allowedTokens || allowedTokens.includes(organizationId);

  return schedulesFromBundle(bundle)
    .filter(
      (schedule) =>
        isTeleconsultoriaAvailabilitySchedule(schedule) &&
        schedule.active !== false,
    )
    .map((schedule) => {
      const guest = guestFromSchedule(schedule);
      if (!guest || !schedule.id) return undefined;
      if (guest.practitioner.reference === params.hostPractitionerRef) {
        return undefined;
      }
      const service = includedHealthcareService(schedule, services);
      if (service?.active === false) return undefined;
      const organizationDisplay = service?.providedBy?.display;
      const organizationId = organizationIdFromReference(
        service?.providedBy?.reference,
      );
      // Sem HCS no bundle não dá para provar a organização: com filtro, descarta.
      if (!isAllowedOrganization(organizationId)) return undefined;
      return {
        scheduleId: schedule.id,
        // A organização vai junto com o convidado: o convite (Task) é para o
        // PractitionerRole dele nessa organização.
        guest: { ...guest, ...(organizationId ? { organizationId } : {}) },
        ...(organizationDisplay ? { organizationDisplay } : {}),
        ...(organizationId ? { organizationId } : {}),
      };
    })
    .filter((item): item is TeleconsultoriaAvailableProfessional =>
      Boolean(item),
    );
}

export async function setTeleconsultoriaScheduleActive(
  schedule: fhir5.Schedule,
  active: boolean,
) {
  if (!schedule.id) throw new Error("Schedule sem id");
  await PatchSchedule(schedule.id, [
    {
      op: schedule.active === undefined ? "add" : "replace",
      path: "/active",
      value: active,
    },
  ]);
}

export function organizationTokenFromId(organizationId?: string): string {
  if (!organizationId) return "";
  if (organizationId.startsWith(`${oids.cnpj}-`)) return organizationId;
  return `${oids.cnpj}-${organizationId}`;
}
